import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

// ---------- Types ----------
export interface CarouselOption {
  value: string;
  label: string;
}

interface FilterCarouselProps {
  options: CarouselOption[];
  defaultValue?: string;
  value?: string;
  onChange?: (value: string, index: number) => void;
  className?: string;
  ariaLabel?: string;
  loop?: boolean;
}

// ---------- Component ----------
const FilterCarousel: React.FC<FilterCarouselProps> = ({
  options,
  defaultValue,
  value: controlledValue,
  onChange,
  className = '',
  ariaLabel = 'انتخاب گزینه',
  loop = false,
}) => {
  // State
  const [internalValue, setInternalValue] = useState(
    defaultValue || options[0]?.value || '',
  );
  const currentValue = controlledValue ?? internalValue;

  // Refs
  const cardRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store latest values in refs to avoid stale closures
  const hasPrevRef = useRef(false);
  const hasNextRef = useRef(false);
  const isAnimatingRef = useRef(false);
  const currentIndexRef = useRef(0);
  const onChangeRef = useRef(onChange);
  const controlledValueRef = useRef(controlledValue);

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const [dragOffset, setDragOffset] = useState(0);

  const ANIMATION_DURATION = 350;
  const DRAG_THRESHOLD = 40;

  // Index from value
  const getIndexFromValue = useCallback(
    (val: string) => {
      const index = options.findIndex(opt => opt.value === val);
      return index >= 0 ? index : 0;
    },
    [options],
  );

  const currentIndex = getIndexFromValue(currentValue);

  const hasPrev = loop || currentIndex > 0;
  const hasNext = loop || currentIndex < options.length - 1;

  // Update refs
  hasPrevRef.current = hasPrev;
  hasNextRef.current = hasNext;
  isAnimatingRef.current = isAnimating;
  currentIndexRef.current = currentIndex;
  onChangeRef.current = onChange;
  controlledValueRef.current = controlledValue;

  const prevItem = hasPrev ? options[currentIndex - 1] : null;
  const nextItem = hasNext ? options[currentIndex + 1] : null;

  // Navigation function
  const navigate = useCallback(
    (direction: 'prev' | 'next') => {
      if (isAnimatingRef.current) return;

      const idx = currentIndexRef.current;
      let newIndex = idx;

      if (direction === 'prev') {
        if (!hasPrevRef.current) return;
        newIndex = idx > 0 ? idx - 1 : loop ? options.length - 1 : idx;
        setAnimationClass('animate-slide-right');
      } else {
        if (!hasNextRef.current) return;
        newIndex = idx < options.length - 1 ? idx + 1 : loop ? 0 : idx;
        setAnimationClass('animate-slide-left');
      }

      const newValue = options[newIndex].value;
      if (controlledValueRef.current === undefined) {
        setInternalValue(newValue);
      }
      onChangeRef.current?.(newValue, newIndex);

      setIsAnimating(true);
      isAnimatingRef.current = true;

      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      animationTimeoutRef.current = setTimeout(() => {
        setAnimationClass('');
        setIsAnimating(false);
        isAnimatingRef.current = false;
      }, ANIMATION_DURATION);
    },
    [options, loop],
  );

  // Drag handlers - using refs to avoid stale closures
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (
        isAnimatingRef.current ||
        (!hasPrevRef.current && !hasNextRef.current)
      )
        return;

      e.preventDefault();
      isDraggingRef.current = true;
      hasMovedRef.current = false;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      startXRef.current = clientX;
      currentXRef.current = clientX;

      document.addEventListener('mousemove', handleDragMove as any);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleDragMove as any, {
        passive: false,
      });
      document.addEventListener('touchend', handleDragEnd);
    },
    [], // No dependencies needed since we use refs
  );

  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDraggingRef.current) return;

    e.preventDefault();
    const clientX =
      'touches' in e
        ? (e as TouchEvent).touches[0].clientX
        : (e as MouseEvent).clientX;
    const diff = clientX - startXRef.current;

    // Constrain drag based on available directions (using refs)
    let constrainedDiff = diff;
    if (!hasPrevRef.current && diff > 0) constrainedDiff = 0;
    if (!hasNextRef.current && diff < 0) constrainedDiff = 0;

    if (Math.abs(diff) > 5) {
      hasMovedRef.current = true;
    }

    currentXRef.current = clientX;
    setDragOffset(constrainedDiff);
  }, []); // No dependencies - uses refs for current values

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;

    document.removeEventListener('mousemove', handleDragMove as any);
    document.removeEventListener('mouseup', handleDragEnd);
    document.removeEventListener('touchmove', handleDragMove as any);
    document.removeEventListener('touchend', handleDragEnd);

    if (!hasMovedRef.current) {
      setDragOffset(0);
      return;
    }

    const diff = currentXRef.current - startXRef.current;

    if (diff > DRAG_THRESHOLD && hasPrevRef.current) {
      navigate('prev');
    } else if (diff < -DRAG_THRESHOLD && hasNextRef.current) {
      navigate('next');
    }

    setDragOffset(0);
  }, [navigate]); // navigate is stable

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isAnimatingRef.current) {
        e.preventDefault();
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigate('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigate('next');
          break;
        case 'Home':
          e.preventDefault();
          if (options.length > 0 && !isAnimatingRef.current) {
            const newValue = options[0].value;
            setAnimationClass('animate-slide-right');
            if (controlledValueRef.current === undefined)
              setInternalValue(newValue);
            onChangeRef.current?.(newValue, 0);
            setIsAnimating(true);
            isAnimatingRef.current = true;
            if (animationTimeoutRef.current)
              clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = setTimeout(() => {
              setAnimationClass('');
              setIsAnimating(false);
              isAnimatingRef.current = false;
            }, ANIMATION_DURATION);
          }
          break;
        case 'End':
          e.preventDefault();
          if (options.length > 0 && !isAnimatingRef.current) {
            const lastIndex = options.length - 1;
            const newValue = options[lastIndex].value;
            setAnimationClass('animate-slide-left');
            if (controlledValueRef.current === undefined)
              setInternalValue(newValue);
            onChangeRef.current?.(newValue, lastIndex);
            setIsAnimating(true);
            isAnimatingRef.current = true;
            if (animationTimeoutRef.current)
              clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = setTimeout(() => {
              setAnimationClass('');
              setIsAnimating(false);
              isAnimatingRef.current = false;
            }, ANIMATION_DURATION);
          }
          break;
      }
    },
    [navigate, options],
  );

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  // Button classes
  const buttonBaseClass = useMemo(
    () =>
      'size-7 flex justify-center items-center rounded-lg text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:cursor-not-allowed z-10',
    [],
  );

  const isPrevDisabled = !hasPrev || isAnimating;
  const isNextDisabled = !hasNext || isAnimating;

  // Calculate drag transform and opacity
  const dragTransform = dragOffset !== 0 ? `translateX(${dragOffset}px)` : '';
  const dragScale = dragOffset !== 0 ? 1 - Math.abs(dragOffset) / 800 : 1;
  const dragOpacity = dragOffset !== 0 ? 1 - Math.abs(dragOffset) / 400 : 1;
  const prevOpacity = dragOffset > 0 ? Math.min(dragOffset / 80, 0.7) : 0.3;
  const nextOpacity =
    dragOffset < 0 ? Math.min(Math.abs(dragOffset) / 80, 0.7) : 0.3;

  return (
    <>
      <style>
        {`
          @keyframes slideLeft {
            0% { transform: translateX(80px); opacity: 0; scale: 0.95; }
            100% { transform: translateX(0); opacity: 1; scale: 1; }
          }
          @keyframes slideRight {
            0% { transform: translateX(-80px); opacity: 0; scale: 0.95; }
            100% { transform: translateX(0); opacity: 1; scale: 1; }
          }
          .animate-slide-left {
            animation: slideLeft 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          }
          .animate-slide-right {
            animation: slideRight 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          }
        `}
      </style>
      <div
        className={`flex items-center justify-between gap-3 select-none ${className}`}
        role="group"
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
        style={{touchAction: 'pan-y'}}
      >
        {/* Next Button */}
        <button
          type="button"
          onClick={() => navigate('next')}
          disabled={isNextDisabled}
          className={`${buttonBaseClass} ${
            !isNextDisabled
              ? 'bg-farm-shop-500 shadow-sm hover:shadow active:scale-85 hover:scale-110'
              : 'bg-gray-600 opacity-50'
          } transition-transform`}
          aria-label="گزینه بعدی"
        >
          <i className=" icon-nameArrow---Right-2" />
        </button>

        {/* Cards container */}
        <div className="relative min-w-[140px] flex items-center justify-center">
          {/* Previous item preview */}
          {prevItem && (
            <span
              className="absolute mr-5 text-lg text-gray-400 transition-opacity duration-200 -translate-y-1/2 right-full top-1/2 whitespace-nowrap"
              style={{opacity: prevOpacity}}
            >
              {prevItem.label}
            </span>
          )}

          {/* Next item preview */}
          {nextItem && (
            <span
              className="absolute ml-5 text-lg text-gray-400 transition-opacity duration-200 -translate-y-1/2 left-full top-1/2 whitespace-nowrap"
              style={{opacity: nextOpacity}}
            >
              {nextItem.label}
            </span>
          )}

          {/* Main card */}
          <div
            ref={cardRef}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            style={{
              transform: dragTransform,
              scale: dragScale.toString(),
              opacity: dragOpacity.toString(),
              transition: !isDraggingRef.current
                ? 'transform 0.2s, opacity 0.2s'
                : 'none',
            }}
            className={`
              w-[100px] py-2 text-center font-medium text-lg rounded-lg
              bg-farm-shop-50 border-farm-shop-500 text-farm-shop-500 border-[1px] shadow-sm
              cursor-grab active:cursor-grabbing
              select-none
              ${isAnimating ? 'pointer-events-none' : ''}
              ${animationClass}
            `}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="px-2">{options[currentIndex]?.label}</span>
          </div>
        </div>

        {/* Previous Button */}
        <button
          type="button"
          onClick={() => navigate('prev')}
          disabled={isPrevDisabled}
          className={`${buttonBaseClass} ${
            !isPrevDisabled
              ? 'bg-farm-shop-500 shadow-sm hover:shadow active:scale-85 hover:scale-110'
              : 'bg-gray-600 opacity-50'
          } transition-transform`}
          aria-label="گزینه قبلی"
        >
          <i className=" icon-nameArrow---Left-2" />
        </button>
      </div>
    </>
  );
};

export default FilterCarousel;
